import { inject, Pipe, PipeTransform } from '@angular/core';
import { AnalyticsService } from '../services/analytics/analytics.service';
import { EventName } from '../services/analytics/event-names';

@Pipe({ name: 'theme', pure: true })
export class ThemePipe implements PipeTransform {
  private analyticsService = inject(AnalyticsService);

  transform(input: [string, string] | [string, string][]): { [key: string]: string } {
    if (!input) return {};

    const pairs: [string, string][] = Array.isArray(input[0])
      ? (input as [string, string][])
      : [input as [string, string]];

    return pairs.reduce((styles, [group, variant]) => {
      const cleanGroup = group?.trim();
      const cleanVariant = variant?.trim();

      if (!cleanGroup || !cleanVariant) {
        this.logMissingThemeError(group, variant);
        return styles;
      }

      let groupStyles: { [key: string]: string } = {};

      switch (cleanGroup) {
        case 'type':
          groupStyles = this.typeStyles(cleanVariant);
          break;
        case 'drop-shadow':
        case 'inner-shadow':
        case 'drop-and-inner-shadow':
          groupStyles = this.shadowStyles(cleanGroup, cleanVariant);
          break;
        case 'text-shadow':
          groupStyles = this.textShadowStyles(cleanVariant);
          break;
        case 'text-outer-border':
          groupStyles = this.textOuterBorderStyles(cleanVariant);
          break;
        case 'text-stroke':
          groupStyles = this.textStrokeStyles(cleanVariant);
          break;
        case 'background':
          groupStyles = this.backgroundStyles(cleanVariant);
          break;
        case 'background-gradient':
          groupStyles = this.backgroundGradientStyles(cleanVariant);
          break;
        case 'border':
          groupStyles = this.borderStyles(cleanVariant);
          break;
        case 'border-bottom':
          groupStyles = this.borderBottomStyles(cleanVariant);
          break;
        case 'border-radius-top':
          groupStyles = this.borderRadiusTopStyles(cleanVariant);
          break;
        case 'text-color':
          groupStyles = this.textColorStyles(cleanVariant);
          break;
        case 'text-decoration':
          groupStyles = this.textDecorationStyles(cleanVariant);
          break;
        case 'border-radius':
          groupStyles = this.borderRadiusStyles(cleanVariant);
          break;
        case 'height':
          groupStyles = this.heightStyles(cleanVariant);
          break;
        case 'size':
          groupStyles = this.sizeStyles(cleanVariant);
          break;
        case 'gap':
          groupStyles = this.gapStyles(cleanVariant);
          break;
        default:
          this.logMissingThemeError(cleanGroup, cleanVariant);
          break;
      }

      return { ...styles, ...groupStyles };
    }, {});
  }

  private logMissingThemeError(group: string | undefined, variant: string | undefined): void {
    this.analyticsService.log(EventName.ThemeError, {
      error: 'Missing theme element',
      group: group || 'undefined',
      variant: variant || 'undefined',
    });
  }

  private typeStyles(variant: string): { [key: string]: string } {
    const prefix = `--${variant}-type`;

    return {
      'font-family': `var(${prefix}-font-family)`,
      'font-weight': `var(${prefix}-font-weight)`,
      'font-size': `var(${prefix}-font-size)`,
      'line-height': `var(${prefix}-line-height)`,
      'text-transform': `var(${prefix}-text-case)`,
      'font-style': `var(${prefix}-font-style)`,
    };
  }

  private textColorStyles(variant: string): { [key: string]: string } {
    return {
      color: `var(--${variant}-fill)`,
    };
  }

  private shadowStyles(group: string, variant: string): { [key: string]: string } {
    const dropShadowPrefix = `--${variant}-drop-shadow`;
    const innerShadowPrefix = `--${variant}-inner-shadow`;

    switch (group) {
      case 'drop-shadow':
        return {
          'box-shadow': `var(${dropShadowPrefix}-offset-x) var(${dropShadowPrefix}-offset-y) var(${dropShadowPrefix}-blur) var(${dropShadowPrefix}-spread) var(${dropShadowPrefix}-color)`,
        };
      case 'inner-shadow':
        return {
          'box-shadow': `inset var(${innerShadowPrefix}-offset-x) var(${innerShadowPrefix}-offset-y) var(${innerShadowPrefix}-blur) var(${innerShadowPrefix}-spread) var(${innerShadowPrefix}-color)`,
        };
      case 'drop-and-inner-shadow': {
        const dropShadow = `var(${dropShadowPrefix}-offset-x) var(${dropShadowPrefix}-offset-y) var(${dropShadowPrefix}-blur) var(${dropShadowPrefix}-spread) var(${dropShadowPrefix}-color)`;
        const innerShadow = `inset var(${innerShadowPrefix}-offset-x) var(${innerShadowPrefix}-offset-y) var(${innerShadowPrefix}-blur) var(${innerShadowPrefix}-spread) var(${innerShadowPrefix}-color)`;
        return {
          'box-shadow': `${dropShadow}, ${innerShadow}`,
        };
      }
      default:
        return {};
    }
  }

  private textShadowStyles(variant: string): { [key: string]: string } {
    const prefix = `--${variant}-drop-shadow`;
    return {
      'text-shadow': `var(${prefix}-offset-x) var(${prefix}-offset-y) var(${prefix}-blur) var(${prefix}-color)`,
    };
  }

  private textOuterBorderStyles(variant: string): { [key: string]: string } {
    const prefix = `--${variant}-border`;
    const color = `var(${prefix}-color)`;
    const width = `var(${prefix}-width)`;

    return {
      'text-shadow': `
        calc(${width}) 0 ${color},
        calc(-1 * ${width}) 0 ${color},
        0 calc(${width}) ${color},
        0 calc(-1 * ${width}) ${color},
        calc(${width}) calc(${width}) ${color},
        calc(-1 * ${width}) calc(${width}) ${color},
        calc(${width}) calc(-1 * ${width}) ${color},
        calc(-1 * ${width}) calc(-1 * ${width}) ${color}
      `,
    };
  }

  private textStrokeStyles(variant: string): { [key: string]: string } {
    const prefix = `--${variant}-border`;
    return {
      '-webkit-text-stroke': `var(${prefix}-width) var(${prefix}-color)`,
      'text-stroke': `var(${prefix}-width) var(${prefix}-color)`,
    };
  }

  private backgroundGradientStyles(variant: string): { [key: string]: string } {
    return {
      background: `var(--${variant}-background-gradient-fill)`,
    };
  }

  private backgroundStyles(variant: string): { [key: string]: string } {
    return {
      background: `var(--${variant}-background-fill)`,
    };
  }

  private borderStyles(variant: string): { [key: string]: string } {
    const prefix = `--${variant}-border`;
    return {
      border: `var(${prefix}-width) var(${prefix}-style) var(${prefix}-color)`,
    };
  }

  private borderRadiusStyles(variant: string): { [key: string]: string } {
    return {
      'border-radius': `var(--${variant}-border-radius)`,
    };
  }

  private borderRadiusTopStyles(variant: string): { [key: string]: string } {
    return {
      'border-top-left-radius': `var(--${variant}-border-radius)`,
      'border-top-right-radius': `var(--${variant}-border-radius)`,
    };
  }

  private borderBottomStyles(variant: string): { [key: string]: string } {
    return {
      'border-bottom': `var(--${variant}-border-width) var(--${variant}-border-style) var(--${variant}-border-color)`,
    };
  }

  private heightStyles(variant: string): { [key: string]: string } {
    return {
      height: `var(--${variant}-height)`,
    };
  }

  private sizeStyles(variant: string): { [key: string]: string } {
    return {
      width: `var(--${variant}-size)`,
      height: `var(--${variant}-size)`,
    };
  }

  private gapStyles(variant: string): { [key: string]: string } {
    return {
      gap: `var(--${variant}-gap)`,
    };
  }

  private textDecorationStyles(variant: string): { [key: string]: string } {
    return {
      'text-decoration': `var(--${variant}-type-text-decoration)`,
    };
  }
}
